import { useState } from "react";
import { comments } from "@/mocks/homeData";

const hueBadge: Record<string, string> = {
  primary: "bg-primary-50 text-primary-700",
  secondary: "bg-secondary-50 text-secondary-700",
  accent: "bg-accent-50 text-accent-700",
};

const hueAvatar: Record<string, string> = {
  primary: "bg-primary-100 text-primary-800",
  secondary: "bg-secondary-100 text-secondary-800",
  accent: "bg-accent-100 text-accent-800",
};

export default function DiscussionSection() {
  const [order, setOrder] = useState<"hot" | "new">("hot");
  const [notice, setNotice] = useState("");

  const sorted = order === "hot" ? comments : [...comments].slice().reverse();

  const handlePost = () => {
    setNotice("讨论区正在做上线前的最后检查，先看看大家怎么说，很快就能开口啦。");
  };

  return (
    <section id="discussion" className="w-full border-y border-background-200 bg-background-100">
      <div className="w-full px-4 py-10 md:px-8 md:py-12">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-md bg-accent-500 text-background-50">
              <i className="ri-chat-smile-2-line text-lg" style={{ lineHeight: 1 }} />
            </span>
            <div className="flex flex-col leading-tight">
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">讨论区 · 现在就聊</h2>
              <span className="text-xs text-foreground-500">
                1.4 万人正在看 · 今日新增 2,317 条讨论
              </span>
            </div>
          </div>

          <div className="flex items-center gap-1 rounded-full border border-background-300 bg-background-50 px-1 py-1">
            <button
              type="button"
              onClick={() => setOrder("hot")}
              className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                order === "hot"
                  ? "bg-primary-500 text-background-50"
                  : "text-foreground-600 hover:text-primary-700"
              }`}
            >
              最热
            </button>
            <button
              type="button"
              onClick={() => setOrder("new")}
              className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                order === "new"
                  ? "bg-primary-500 text-background-50"
                  : "text-foreground-600 hover:text-primary-700"
              }`}
            >
              最新
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_320px]">
          <div>
            <div className="rounded-lg border border-background-200 bg-background-50 p-4">
              <div className="flex items-start gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-100 font-label text-sm font-bold text-primary-800">
                  我
                </span>
                <div className="flex-1">
                  <input
                    type="text"
                    value={notice}
                    onChange={(event) => setNotice(event.target.value)}
                    placeholder="说说你对这条瓜料时间线的看法…"
                    aria-label="发表讨论"
                    className="w-full rounded-md border border-background-200 bg-background-50 px-3 py-3 text-sm text-foreground-800 outline-none placeholder:text-foreground-400 focus:border-primary-400"
                  />
                  <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
                    <div className="flex flex-wrap items-center gap-2">
                      {["补充线索", "求完整时间线", "理性发言"].map((chip) => (
                        <button
                          key={chip}
                          type="button"
                          className="cursor-pointer whitespace-nowrap rounded-full bg-background-100 px-3 py-1.5 text-xs text-foreground-600 transition-colors hover:bg-secondary-100 hover:text-secondary-800"
                        >
                          {chip}
                        </button>
                      ))}
                    </div>
                    <button
                      type="button"
                      onClick={handlePost}
                      className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                    >
                      发表
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-4 flex flex-col divide-y divide-background-200 overflow-hidden rounded-lg border border-background-200 bg-background-50">
              {sorted.map((item, index) => (
                <article
                  key={item.name}
                  className="reveal flex gap-3 p-4"
                  style={{ animationDelay: `${index * 60}ms` }}
                >
                  <span
                    className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full font-heading text-base font-bold ${hueAvatar[item.hue]}`}
                  >
                    {item.initial}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-semibold text-foreground-900">{item.name}</span>
                      <span className={`rounded-sm px-2 py-0.5 text-[11px] ${hueBadge[item.hue]}`}>
                        {item.badge}
                      </span>
                      <span className="font-label text-xs text-foreground-400">{item.time}</span>
                    </div>
                    <p className="mt-1.5 text-sm leading-relaxed text-foreground-700">{item.content}</p>
                    <div className="mt-2 flex items-center gap-5 text-xs text-foreground-500">
                      <button
                        type="button"
                        className="flex cursor-pointer items-center gap-1 transition-colors hover:text-accent-600"
                      >
                        <i className="ri-thumb-up-line h-4 w-4" style={{ lineHeight: 1 }} />
                        {item.likes}
                      </button>
                      <button
                        type="button"
                        className="flex cursor-pointer items-center gap-1 transition-colors hover:text-primary-600"
                      >
                        <i className="ri-chat-3-line h-4 w-4" style={{ lineHeight: 1 }} />
                        {item.replies} 条回复
                      </button>
                      <button
                        type="button"
                        className="flex cursor-pointer items-center gap-1 transition-colors hover:text-foreground-900"
                      >
                        <i className="ri-share-forward-line h-4 w-4" style={{ lineHeight: 1 }} />
                        分享
                      </button>
                    </div>
                  </div>
                </article>
              ))}
            </div>

            <button
              type="button"
              className="mt-4 w-full cursor-pointer whitespace-nowrap rounded-md border border-background-300 bg-background-50 py-3 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
            >
              查看全部 2,317 条讨论
            </button>
          </div>

          <aside className="flex flex-col gap-4">
            <div className="rounded-lg border border-background-200 bg-background-50 p-4">
              <div className="flex items-center gap-2">
                <i className="ri-user-star-line h-5 w-5 text-primary-600" style={{ lineHeight: 1 }} />
                <h3 className="text-sm font-semibold text-foreground-900">活跃发言人</h3>
              </div>
              <ul className="mt-3 flex flex-col gap-3">
                {[
                  { name: "只看脉络的老哥", tag: "时间线党", value: "862 赞" },
                  { name: "抠细节的路人", tag: "细节党", value: "641 赞" },
                  { name: "专门记反转", tag: "反转记录员", value: "503 赞" },
                ].map((user) => (
                  <li key={user.name} className="flex items-center gap-3">
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-secondary-100 font-heading text-sm font-bold text-secondary-800">
                      {user.name.slice(0, 1)}
                    </span>
                    <span className="flex min-w-0 flex-1 flex-col">
                      <span className="truncate text-sm text-foreground-800">{user.name}</span>
                      <span className="text-[11px] text-foreground-500">{user.tag}</span>
                    </span>
                    <span className="shrink-0 font-label text-xs text-foreground-400">{user.value}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-lg border border-background-200 bg-secondary-50 p-4">
              <div className="flex items-center gap-2">
                <i className="ri-book-open-line h-5 w-5 text-secondary-700" style={{ lineHeight: 1 }} />
                <h3 className="text-sm font-semibold text-foreground-900">讨论公约</h3>
              </div>
              <ul className="mt-3 flex flex-col gap-2 text-xs leading-relaxed text-foreground-600">
                <li className="flex gap-2">
                  <i className="ri-check-line mt-0.5 h-4 w-4 shrink-0 text-secondary-600" style={{ lineHeight: 1 }} />
                  只讨论已公开的材料，不传播未经证实的说法。
                </li>
                <li className="flex gap-2">
                  <i className="ri-check-line mt-0.5 h-4 w-4 shrink-0 text-secondary-600" style={{ lineHeight: 1 }} />
                  补充线索请附带时间与出处，方便他人核对。
                </li>
                <li className="flex gap-2">
                  <i className="ri-check-line mt-0.5 h-4 w-4 shrink-0 text-secondary-600" style={{ lineHeight: 1 }} />
                  允许观点不同，但请对事不对人。
                </li>
              </ul>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}